import { Badge, Loader, Stack, Table, Tooltip, Whisper } from 'rsuite';
import ProvisionService from './Provision';
import Instance from './Instance';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { DeviceStatus, getRowFullAccess } from '@monorepo/shared';
import { useAppSelector } from '@monorepo/shared/store/hook';
import {
  selectInstances,
  selectRejectedInstances
} from '@monorepo/shared/store/instanceSlice';
import { selectInstancesExtraInfo } from '@monorepo/shared/store/instanceExtraSlice';
import { selectCurrentGroup } from '@monorepo/shared/store/groupSlice';
import { selectAssets } from '@monorepo/shared/store/assetSlice';
import { t } from 'i18next';
import { selectRejectedRegions } from '@monorepo/shared/store/authSlice';
import {
  selectAccountWithPermission,
  selectSystem
} from '@monorepo/shared/store/accountSlice';
import { HomeElms } from '../../config/const';

const { Column, HeaderCell, Cell } = Table;

export const ExpandedRow = ({ rowData }: IRowProps) => {
  const systemData = useAppSelector(selectSystem);
  const instances = useAppSelector(selectInstances);
  const rejectedRegions = useAppSelector(selectRejectedRegions);
  const rejectedInstances = useAppSelector(selectRejectedInstances);
  const currentGroups = useAppSelector(selectCurrentGroup);
  const accountWithPermission = useAppSelector(selectAccountWithPermission);
  const [isMaster, setIsMaster] = useState<boolean>(false);
  const [isProvisioned, setIsProvisioned] = useState<boolean>(false);
  const [isFullAccess, setIsFullAccess] = useState<boolean>(false);
  const [isRejectedRegion, setIsRejectedRegion] = useState<boolean>(false);
  const [rejectedData, setRejectedData] = useState<Record<string, any>>({});
  // const [hasInstance, setHasInstance] = useState<boolean>(false);
  const [contentLeftPadding, setContentLeftPadding] = useState<CSSProperties>(
    {}
  );
  const [instance, setInstance] = useState<Record<string, any>>({});
  useEffect(() => {
    const inst = instances?.[currentGroups.id]?.[rowData?.account_id];
    const rejectedIns =
      rejectedInstances?.[currentGroups.id]?.[rowData?.account_id] || {};
    const rejectedRegionNames = Object.keys(rejectedRegions);
    setIsRejectedRegion(
      rejectedRegionNames.length > 0 &&
        rejectedRegionNames.includes(rowData?.region?.alias)
    );
    setRejectedData(rejectedIns);
    setIsProvisioned(rowData?.region && rowData?.region?.url);
    // setHasInstance(!!inst);
    if (!!inst) {
      setInstance(inst);
    }
    setIsMaster(rowData?.user_type === 'Master');
    setIsFullAccess(
      getRowFullAccess(rowData, accountWithPermission, HomeElms?.fcName) ||
        rowData?.user_fullaccess ||
        accountWithPermission?.user_fullaccess
    );
    setContentLeftPadding({ paddingLeft: rowData?.level * 30 + 5 + 'px' });
  }, [
    rowData,
    accountWithPermission,
    instances,
    currentGroups,
    rejectedRegions,
    rejectedInstances
  ]);

  const getContent = () => {
    if (isProvisioned) {
      if (instance?.task_id) {
        const taskObj = {
          task_id: instance.task_id,
          account_id: rowData?.account_id
        };
        return (
          <ProvisionService rowData={rowData || {}} existedTaskObj={taskObj} />
        );
      } else {
        return <Instance rowData={{ ...rowData, isFullAccess }} />;
      }
    } else {
      if (isMaster) {
        return <ProvisionService rowData={rowData || {}} />;
      } else if (systemData?.enable_iam_provision && isFullAccess) {
        return <ProvisionService rowData={rowData || {}} />;
      } else {
        return (
          <Stack justifyContent="center" style={{ height: '100%' }}>
            <p>{t('common.msgConst.sub_user_provision_decline')}</p>
          </Stack>
        );
      }
    }
  };
  return (
    <div className="account-expand-content" style={contentLeftPadding}>
      {getContent()}
    </div>
  );
};
export const renderNormalRow = (ouLevel: number, isGroupView?: boolean) => {
  const ouWidth: number = ouLevel * 30 + 280;
  const getAccountRow = (
    rowData: Record<string, any>,
    keyName: string,
    style?: CSSProperties
  ) => {
    return (
      <span
        className={rowData?.account_id ? 'account-row' : ''}
        style={style}
        title={rowData?.[keyName]}
      >
        {rowData?.[keyName]}
      </span>
    );
  };

  const getColHeaderWithTooltip = (content: string) => {
    return (
      <Whisper
        placement="top"
        controlId="control-id-hover"
        trigger="hover"
        speaker={<Tooltip>{content}</Tooltip>}
      >
        <span>{content}</span>
      </Whisper>
    );
  };

  return (
    <>
      <Column
        width={ouWidth}
        minWidth={ouWidth}
        fixed
        sortable={isGroupView || ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('faz.organization.cols.ou'))}
        </HeaderCell>
        <Cell dataKey="account_company">
          {(rowData) => {
            const level = rowData?.level || 0;
            const width = ouWidth - level * 30 - 50;
            const icon = rowData?.account_id ? (
              <i
                className="icon-fontello-doc"
                style={{ fontSize: '20px', color: '#848baa' }}
              />
            ) : rowData?.rootNode ? (
              <i
                className="icon-fontello-rootfolder"
                style={{ fontSize: '22px', color: '#848baa' }}
              />
            ) : (
              <i
                className="icon-fontello-folder"
                style={{ fontSize: '20px', color: '#848baa' }}
              />
            );
            return (
              <div
                className={rowData?.account_id ? 'account-row' : ''}
                style={
                  rowData?.children
                    ? {
                        width: width,
                        overflow: 'hidden',
                        display: 'inline-flex'
                      }
                    : {
                        width: width,
                        overflow: 'hidden',
                        display: 'inline-flex',
                        paddingLeft: '26px'
                      }
                }
              >
                {icon}
                <div
                  style={{
                    display: 'flex'
                  }}
                >
                  <div
                    style={{
                      color: '#212121',
                      maxWidth: rowData?.account_id ? width - 80 : width,
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                    title={rowData?.ou}
                  >
                    {rowData?.ou}
                  </div>
                  {getInfoIcons(rowData)}
                </div>
              </div>
            );
          }}
        </Cell>
      </Column>
      <Column
        width={160}
        minWidth={160}
        flexGrow={1}
        sortable={isGroupView || ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('faz.organization.cols.accountId'))}
        </HeaderCell>
        <Cell dataKey="account_id">
          {(rowData) => {
            const user_type =
              rowData?.user_type === 'Master'
                ? 'Primary'
                : rowData?.user_type === 'ThirdParty' && rowData?.idp_role_name
                  ? rowData?.idp_role_name
                  : rowData?.user_type;
            return (
              <Stack spacing={6}>
                <span className={rowData?.account_id ? 'account-row' : ''}>
                  <div style={{ display: 'inline-block', minWidth: '60px' }}>
                    {rowData?.account_id}
                  </div>
                </span>
                {rowData?.user_type && (
                  <Badge
                    style={{
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: '2px 10px',
                      borderRadius: '10px',
                      backgroundColor:
                        rowData?.user_type === 'Master'
                          ? 'var(--rs-blue-400)'
                          : 'var(--rs-green-400)'
                    }}
                    content={user_type}
                  />
                )}
              </Stack>
            );
          }}
        </Cell>
      </Column>
      <Column
        width={160}
        minWidth={160}
        flexGrow={1}
        sortable={isGroupView || ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('faz.organization.cols.owner'))}
        </HeaderCell>
        <Cell dataKey="master_user_name">
          {(rowData) => getAccountRow(rowData, 'master_user_name')}
        </Cell>
      </Column>
      <Column
        width={160}
        minWidth={160}
        flexGrow={1}
        sortable={!isGroupView && ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('faz.organization.cols.serviceRegion'))}
        </HeaderCell>
        <Cell dataKey="regionName">
          {(rowData) => getAccountRow(rowData, 'regionName')}
        </Cell>
      </Column>
      <Column
        width={120}
        minWidth={120}
        sortable={!isGroupView && ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('faz.organization.cols.numberOfDevice'))}
        </HeaderCell>
        <Cell dataKey="deviceNum">
          {(rowData) => <AssetCol rowData={rowData} name="deviceNum" />}
        </Cell>
      </Column>
      <Column width={120} minWidth={120} flexGrow={1}>
        <HeaderCell>
          {getColHeaderWithTooltip(t('faz.organization.cols.loggingStatus'))}
        </HeaderCell>
        <Cell>
          {(rowData) => <AssetCol rowData={rowData} name="deviceHealth" />}
        </Cell>
      </Column>
      <Column
        width={100}
        minWidth={100}
        flexGrow={1}
        sortable={!isGroupView && ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('faz.organization.cols.logRate'))}
        </HeaderCell>
        <Cell dataKey="resourceSpeed">
          {(rowData) => <InstanceCol rowData={rowData} name="resourceSpeed" />}
        </Cell>
      </Column>
      <Column
        width={160}
        minWidth={160}
        flexGrow={1}
        sortable={!isGroupView && ouLevel === 0}
      >
        <HeaderCell>
          {getColHeaderWithTooltip(t('faz.organization.cols.size'))}
        </HeaderCell>
        <Cell dataKey="resourceSizeGB">
          {(rowData) => <InstanceCol rowData={rowData} name="resourceSizeGB" />}
        </Cell>
      </Column>
    </>
  );
};

interface IColProps {
  rowData: Record<string, any>;
  name: string;
}
interface IRowProps {
  rowData: Record<string, any>;
}
const InstanceCol = ({ rowData, name }: IColProps) => {
  const [displayIns, setDisplayIns] = useState<Record<string, any>>({});
  const instances = useAppSelector(selectInstances);
  const currentGroups = useAppSelector(selectCurrentGroup);

  const initData = () => {
    if (currentGroups?.id && rowData?.account_id) {
      const inst = instances?.[currentGroups.id]?.[rowData?.account_id];
      if (inst) {
        setDisplayIns({ ...inst });
      }
    }
  };

  useEffect(() => {
    initData();
  }, [rowData?.account_id]);
  useEffect(() => {
    initData();
  }, [instances, currentGroups]);

  return rowData?.account_id ? (
    <span className="account-row">{displayIns[name]}</span>
  ) : (
    <span></span>
  );
};

const AssetCol = ({ rowData, name }: IColProps) => {
  const [displayAsset, setDisplayAsset] = useState<Record<string, any>>({});
  const currentGroups = useAppSelector(selectCurrentGroup);
  const assets = useAppSelector(selectAssets);
  const instances = useAppSelector(selectInstances);

  const initData = () => {
    if (currentGroups?.id && rowData?.account_id) {
      const asset = assets?.[currentGroups.id]?.[rowData?.account_id];
      const instance = instances?.[currentGroups.id]?.[rowData?.account_id];
      if (asset) {
        setDisplayAsset({ ...asset, hideStatus: instance?.hide_status });
      }
    }
  };

  useEffect(() => {
    initData();
  }, [rowData?.account_id]);

  useEffect(() => {
    initData();
  }, [assets, instances, currentGroups]);

  const getContent = (name: string) => {
    if (name === 'deviceHealth') {
      let content = <></>;
      const obj = displayAsset[name];
      if (displayAsset.hideStatus === false && obj && Object.keys(obj).length) {
        const items = Object.keys(obj).map((key, idx) => {
          const num: number = obj[key] as number;
          let device_status: string = key;
          return num > 0 ? (
            <DeviceStatus
              deviceData={
                device_status === 'inactive' ? obj.inactive_sn : obj.active_sn
              }
              device_status={device_status}
              device_num={
                device_status === 'inactive' ? obj.inactive : obj.active
              }
              key={idx}
            />
          ) : (
            ''
          );
        });
        content = <Stack>{items}</Stack>;
      } else {
        content = <span> - </span>;
      }
      return (
        <div className={rowData?.account_id ? 'account-row' : ''}>
          {content}
        </div>
      );
    }
    return displayAsset[name];
  };
  return rowData?.account_id ? (
    <span className="account-row">{getContent(name)}</span>
  ) : (
    <span></span>
  );
};

const getInfoIcons = (rowData: Record<string, any>) => {
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isExpiring, setIsExpiring] = useState<boolean>(false);
  const [hasEntitleAlert, setHasEntitleAlert] = useState<boolean>(false);
  const instances = useAppSelector(selectInstances);
  const instancesExtraInfo = useAppSelector(selectInstancesExtraInfo);
  const currentGroups = useAppSelector(selectCurrentGroup);
  const [upgradeInfo, setUpgradeInfo] = useState<Record<string, any>>({});
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    const inst = instances?.[currentGroups.id]?.[rowData?.account_id];
    const extraInfo =
      instancesExtraInfo?.[currentGroups.id]?.[rowData?.account_id];
    if (inst) {
      const license = inst.detail?.license || {};
      const isExpiring = license.isExpiring;
      const isExpired = license.isExpired;
      const hasEntitleAlert = license.hasEntitleAlert;
      setIsExpired(isExpired);
      setIsExpiring(isExpiring);
      setIsDisabled(inst?.is_expired);
      setHasEntitleAlert(hasEntitleAlert);
    }
    if (extraInfo) {
      setUpgradeInfo({
        ...extraInfo?.version_upgrade,
        need_force_upgrade: extraInfo?.need_force_upgrade
      });
    }
  }, [instances, currentGroups, instancesExtraInfo]);

  return (
    <Stack spacing={6} style={{ display: 'inline-flex', marginLeft: '6px' }}>
      {hasEntitleAlert && (
        <Whisper
          placement="top"
          controlId="control-id-hover"
          trigger="hover"
          speaker={<Tooltip>{t('common.orgFilter.hasAlarm')}</Tooltip>}
        >
          <i className="icon-fontello-alert" style={{ color: '#ffb300' }}></i>
        </Whisper>
      )}
      {isExpiring && !isExpired && (
        <Whisper
          placement="top"
          controlId="control-id-hover"
          trigger="hover"
          speaker={<Tooltip>{t('common.orgFilter.licenseExpiring')}</Tooltip>}
        >
          <i
            className="icon-fontello-expiring"
            style={{ color: '#ffb300' }}
          ></i>
        </Whisper>
      )}
      {isExpired && (
        <Whisper
          placement="top"
          controlId="control-id-hover"
          trigger="hover"
          speaker={<Tooltip>{t('common.orgFilter.licenseExpired')}</Tooltip>}
        >
          <i className="icon-fontello-expired" style={{ color: '#f44336' }}></i>
        </Whisper>
      )}
      {!isDisabled &&
        (upgradeInfo?.state === 'NEED_UPGRADE' ||
          upgradeInfo?.need_force_upgrade) && (
          <Whisper
            placement="top"
            controlId="control-id-hover"
            trigger="hover"
            speaker={
              <Tooltip>
                {upgradeInfo?.need_force_upgrade
                  ? t(`common.osUpgradeDialog.forceUpgrade`)
                  : t('common.osUpgradeDialog.needUpgrade')}
              </Tooltip>
            }
          >
            <i
              className="icon-fortinetone-upgradeIcon"
              style={{
                color: upgradeInfo?.need_force_upgrade ? '#f44336' : '#ffb300',
                lineHeight: 'revert'
              }}
            ></i>
          </Whisper>
        )}
    </Stack>
  );
};
